import { useState, useRef, useEffect } from "react";
import { PiNotebookLight } from "react-icons/pi";
import { FaRegTrashAlt } from "react-icons/fa";
import { RiDraggable } from "react-icons/ri";
import { IoIosClose } from "react-icons/io";
import { NotebookPen, Copy, Check } from "lucide-react";

import { getClipboardTerms, deleteFromClipboard } from "../../api/universal";
import { IconPill } from "../Universal/IconPill";
import { usePanels } from "../contexts/PanelsContext";

const PopUpNotes = ({ buttonNavbar = false, iconOnly = false }) => {
  const { isNotesOpen, setIsNotesOpen } = usePanels();

  const [clipboardTerms, setClipboardTerms] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [initialMousePos, setInitialMousePos] = useState({ x: 0, y: 0 });
  const [isOpen, setIsOpen] = useState(
    () => JSON.parse(localStorage.getItem("clipboardIsOpen")) || false,
  );
  const [dimensions, setDimensions] = useState(
    () =>
      JSON.parse(localStorage.getItem("clipboardDimensions")) || {
        width: 400,
        height: 480,
      },
  );

  const dropdownRef = useRef(null);
  const dialogId = "notes-window";

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("clipboardPosition"));
      if (saved && typeof saved.x === "number" && typeof saved.y === "number") {
        setPosition(saved);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const response = await getClipboardTerms();
        const terms =
          response?.data?.map((term) => ({ ts: term.date, term: term.term })) ||
          [];
        setClipboardTerms(terms);
      } catch (e) {
        console.log(e);
      }
    })();
  }, [isOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        localStorage.setItem("clipboardIsOpen", JSON.stringify(false));
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const toggleDropdown = () => {
    const next = !isOpen;
    setIsOpen(next);
    localStorage.setItem("clipboardIsOpen", JSON.stringify(next));
  };

  const handleMouseDown = (e) => {
    if (e.target.closest(".drag-handle")) {
      setIsDragging(true);
      setInitialMousePos({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const dx = e.clientX - initialMousePos.x;
    const dy = e.clientY - initialMousePos.y;
    const newX = position.x + dx;
    const newY = position.y + dy;

    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const elementWidth = dropdownRef.current?.offsetWidth ?? 0;
    const elementHeight = dropdownRef.current?.offsetHeight ?? 0;

    const newPosition = {
      x: Math.max(0, Math.min(newX, windowWidth - elementWidth)),
      y: Math.max(0, Math.min(newY, windowHeight - elementHeight)),
    };

    setPosition(newPosition);
    setInitialMousePos({ x: e.clientX, y: e.clientY });
    localStorage.setItem("clipboardPosition", JSON.stringify(newPosition));
  };

  useEffect(() => {
    const up = () => setIsDragging(false);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", up);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", up);
    };
  }, [isDragging, initialMousePos, position]);

  const handleResize = () => {
    if (!dropdownRef.current) return;
    const newDimensions = {
      width: dropdownRef.current.offsetWidth,
      height: dropdownRef.current.offsetHeight,
    };
    setDimensions(newDimensions);
    localStorage.setItem("clipboardDimensions", JSON.stringify(newDimensions));
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver(handleResize);
    if (dropdownRef.current) resizeObserver.observe(dropdownRef.current);
    return () => {
      if (dropdownRef.current) resizeObserver.unobserve(dropdownRef.current);
    };
  }, [dropdownRef.current]);

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
      try {
        if (navigator.vibrate) navigator.vibrate(6);
      } catch {}
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const removeFromClipboard = (text) => {
    try {
      deleteFromClipboard(text);
      setClipboardTerms((prev) => prev.filter((t) => t.term !== text));
    } catch (e) {
      console.log(e);
    }
  };

  // VN icon button pattern (for navbar icon-only usage)
  const iconBtnClasses = [
    "flex items-center justify-center w-8 h-8 rounded-lg",
    "border border-transparent bg-transparent",
    "text-mutedText transition hover:bg-mainButtonBg/50 hover:text-mainText",
    "outline-none ring-inputFocus/60 focus-visible:ring-2",
    "cursor-pointer",
  ].join(" ");

  const renderButton = () => {
    if (iconOnly) {
      return (
        <button
          type="button"
          onClick={toggleDropdown}
          className={iconBtnClasses}
          aria-label={isOpen ? "Close notes" : "Open notes"}
          aria-expanded={isOpen}
          aria-controls={dialogId}
          title="Notes"
        >
          <NotebookPen
            strokeWidth={1.5}
            className={[
              "h-[18px] w-[18px] transition-colors duration-200",
              isOpen ? "text-velBlue" : "text-mutedText",
            ].join(" ")}
          />
        </button>
      );
    }

    if (buttonNavbar) {
      return (
        <IconPill size="md" onClick={toggleDropdown} aria-label="Open notes">
          <NotebookPen
            strokeWidth={1}
            className={[
              "h-5 w-5 shrink-0 transition-colors duration-200",
              isOpen ? "text-velBlue" : "text-mutedText/60",
            ].join(" ")}
          />
        </IconPill>
      );
    }

    // Sidebar full-width button
    return (
      <button
        type="button"
        onClick={toggleDropdown}
        aria-label={isOpen ? "Close notes" : "Open notes"}
        aria-expanded={isOpen}
        aria-controls={dialogId}
        className={[
          "relative group w-full rounded-lg border px-4 py-2 transition-all duration-200 cursor-pointer",
          isOpen
            ? "bg-velBlue/15 border-velBlue/30"
            : "bg-transparent border-surfaceBorder hover:bg-btnHover hover:border-velBlueSecond/25",
        ].join(" ")}
      >
        <div className="relative z-10 flex items-center gap-x-3 min-h-[32px]">
          <PiNotebookLight
            className={[
              "h-5 w-5 shrink-0",
              isOpen ? "text-velBlue" : "text-mutedText/60",
            ].join(" ")}
          />
          <span
            className={[
              "truncate text-[15px] font-normal tracking-wide",
              isOpen ? "text-velBlue" : "text-mutedText",
            ].join(" ")}
          >
            Notes
          </span>
        </div>
      </button>
    );
  };

  return (
    <div>
      {renderButton()}

      {isOpen && (
        <div
          id={dialogId}
          ref={dropdownRef}
          onMouseDown={handleMouseDown}
          role="dialog"
          aria-modal="true"
          aria-labelledby="notes-window-title"
          style={{
            left: position.x,
            top: position.y,
            width: `${dimensions.width}px`,
            height: `${dimensions.height}px`,
          }}
          className="fixed z-50 flex flex-col overflow-hidden rounded-lg border border-surfaceBorder bg-popUpbg/90 shadow-login-panel backdrop-blur-md"
        >
          {/* Drag handle header */}
          <div
            className="drag-handle cursor-grab active:cursor-grabbing flex items-center justify-between gap-2 px-4 py-3 border-b border-dropdownDivider bg-surface/80"
            aria-label="Drag to move notes window"
          >
            <div className="flex items-center gap-2">
              <PiNotebookLight className="h-5 w-5 text-velBlue" />
              <p
                id="notes-window-title"
                className="text-sm font-normal text-mainText"
              >
                Notes
              </p>
              <span className="text-xs text-mutedText/60">
                {clipboardTerms.length} saved
              </span>
            </div>
            <div className="flex items-center gap-1">
              <RiDraggable
                className="h-5 w-5 rotate-90 text-mutedText/60"
                aria-hidden="true"
              />
              <button
                type="button"
                onClick={toggleDropdown}
                aria-label="Close notes"
                className="p-1 rounded-lg transition hover:bg-btnHover cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2"
              >
                <IoIosClose className="h-6 w-6 text-mainText" />
              </button>
            </div>
          </div>

          {/* Note list */}
          <div className="flex-1 overflow-auto p-3 space-y-2">
            {clipboardTerms && clipboardTerms.length > 0 ? (
              <ul className="flex flex-col gap-2">
                {clipboardTerms.map((item, index) => (
                  <li key={`${item.ts}-${index}`}>
                    <div className="relative group rounded-lg border border-inputBorder bg-transparent px-3 py-2.5 transition-all duration-200 hover:border-surfaceBorder hover:bg-selectedSection">
                      <div className="flex items-start justify-between gap-3">
                        <p className="flex-1 text-sm font-normal leading-relaxed break-words text-mainText">
                          {item.term}
                        </p>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => copyToClipboard(item.term, index)}
                            className="p-1.5 rounded-lg transition hover:bg-btnHover cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2"
                            aria-label="Copy to clipboard"
                            title="Copy"
                          >
                            {copiedIndex === index ? (
                              <Check className="h-4 w-4 text-activeCase" />
                            ) : (
                              <Copy className="h-4 w-4 text-mutedText" />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => removeFromClipboard(item.term)}
                            className="p-1.5 rounded-lg transition hover:bg-errorText/10 cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2"
                            aria-label="Remove note"
                            title="Delete"
                          >
                            <FaRegTrashAlt className="h-3.5 w-3.5 text-mutedText" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                <PiNotebookLight
                  className="h-12 w-12 mb-3 text-mutedText/40"
                  aria-hidden="true"
                />
                <p className="text-sm font-normal text-mutedText">
                  No saved notes yet
                </p>
                <p className="text-xs font-normal mt-1 text-mutedText/60">
                  Highlight text anywhere to save notes
                </p>
              </div>
            )}
          </div>

          {/* Footer hint */}
          <div className="px-4 py-2 border-t border-dropdownDivider bg-surface/80">
            <p className="text-xs font-normal text-center text-mutedText/60">
              Drag header to move · Resize from edges
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PopUpNotes;
